// TopSection.jsx
import React, { useState } from "react";
import RegisterYourInterestForm from "../../../../components/RegisterYourInterestForm";

const MA_APPLY_URL = "https://admissions.eusbd.eu/esbd-apply/MA-GBSL/";

function getHeroPair(topBgImage) {
  // Object form
  if (topBgImage && typeof topBgImage === "object") {
    const hi = topBgImage.image || topBgImage.image800 || "";
    let low = topBgImage.image400 || "";
    if (!low && hi) {
      const lastSlash = hi.lastIndexOf("/");
      const dir = hi.slice(0, lastSlash + 1);
      const file = hi.slice(lastSlash + 1);
      const dot = file.lastIndexOf(".");
      const name = dot > -1 ? file.slice(0, dot) : file;
      low = `${dir}out/${name}-400.jpg`;
    }
    return { low, hi };
  }

  // String form
  if (typeof topBgImage === "string" && topBgImage.trim()) {
    const hi = topBgImage.trim();
    const lastSlash = hi.lastIndexOf("/");
    const dir = hi.slice(0, lastSlash + 1);
    const file = hi.slice(lastSlash + 1);
    const dot = file.lastIndexOf(".");
    const name = dot > -1 ? file.slice(0, dot) : file;
    const low = `${dir}out/${name}-400.jpg`;
    return { low, hi };
  }

  return { low: "", hi: "" };
}

const TopSection = ({ topItems = [], title, blab, topBgImage }) => {
  const { low, hi } = getHeroPair(topBgImage);
  const [hiReady, setHiReady] = useState(!low); 
  const mid = Math.ceil(topItems.length / 2);
  const leftItems = topItems.slice(0, mid);
  const rightItems = topItems.slice(mid);

  return (
    <div className="overflow-x-hidden">
      <div className="relative min-h-[65rem] md:min-h-[34rem] overflow-hidden">
        {low && (
          <img
            src={low}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-cover will-change-transform transition-opacity duration-500 ${
              hiReady ? "opacity-0" : "opacity-100"
            } blur-[2px] scale-105`}
            loading="eager"
            decoding="async"
          />
        )}
        {hi && (
          <img
            src={hi}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              hiReady ? "opacity-100" : "opacity-0"
            }`}
            loading="eager"
            decoding="async"
            fetchpriority="high"
            onLoad={() => setHiReady(true)}
            onError={() => {
              console.warn("Hero high-res failed to load:", hi);
            }}
          />
        )}
        <div className="absolute inset-0 bg-black/40 z-10" />

        <div className="absolute inset-0 z-20">
          <div className="relative max-w-6xl mx-auto flex h-full min-h-[60vh]">
            <div className="mx-auto flex flex-col md:flex-row items-center justify-center h-full text-white w-full">
              <div className="w-full md:w-1/2 flex flex-col items-center md:items-start justify-center text-center md:text-left px-6 md:px-20 py-6">
                <h2 className="md:text-3xl text-xl font-bold mb-4">{title}</h2>

                <div className="flex gap-3 md:gap-4">
                  <a href={MA_APPLY_URL} aria-label="Apply Now" target="_blank" rel="noreferrer">
                    <button className="px-4 py-2 rounded border border-white bg-black text-white hover:bg-white hover:text-black transition">
                      Apply Now
                    </button>
                  </a>

                  <a
                    href="/documents/ESBD MA Brochure.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <button className="px-4 py-2 rounded border backdrop-blur-sm border-white bg-transparent text-white hover:bg-white hover:text-black transition">
                      Download Brochure
                    </button>
                  </a>
                </div>
              </div>
              <div className="w-full md:w-1/2 py-6 flex items-center justify-center">
                <div className="flex flex-col md:flex-row justify-center w-full px-4 md:px-0 gap-4">
                  <div className="w-full md:w-5/12">
                    <ul className="space-y-4">
                      {leftItems.map((item, i) => (
                        <li
                          key={`left-${i}`}
                          className="p-2 bg-white/10 border-b-2 backdrop-blur-sm border-white rounded-t-lg text-center md:text-left"
                        >
                          <span className="font-semibold text-lg md:text-xl">
                            {item.title}:{" "}
                          </span>
                          <p className="text-sm md:text-base text-white/90">
                            {item.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="hidden md:flex w-1/12 items-center justify-center">
                    <div className="w-px h-full bg-white/60" />
                  </div>
                  <div className="w-full md:w-5/12">
                    <ul className="space-y-4 flex flex-col justify-between">
                      {rightItems.map((item, i) => (
                        <li
                          key={`right-${i}`}
                          className="p-2 backdrop-blur-sm bg-white/10 border-b-2 border-white rounded-t-lg text-center md:text-left"
                        >
                          <span className="font-semibold text-lg md:text-xl">
                            {item.title}:{" "}
                          </span>
                          <p className="text-sm md:text-base text-white/90">
                            {item.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomSide blab={blab} />
    </div>
  );
};

export default TopSection;

function BottomSide({ blab = {} }) {
  const points = Array.isArray(blab.listBelowBlab) ? blab.listBelowBlab : [];

  return (
    <div>
      <div className="md:hidden px- py-3">
        <RegisterYourInterestForm />
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-0">
        <div className="md:grid md:grid-cols-2 md:gap-8 items-start">
          <div className="flex items-start justify-center md:p-8 py-8">
            <div className="space-y-6 w-full">
              <div className="space-y-2">
                <p className="text-lg md:text-xl text-gray-900 font-bold">
                  {blab?.start}
                </p>
                <p className="text-sm md:text-base text-gray-700">
                  {blab?.finish}
                </p>
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold mb-4">
                  This is European School of Business & Diplomacy (ESBD):
                </h2>
                <div className="w-10 h-0.5 bg-black mb-4" />

                <div className="relative">
                  <div className="overflow-y-auto max-h-[60vh] md:max-h-[120vh] pr-2 masscrollbar-custom">
                    {points.map((item, index) => (
                      <div key={index} className="mb-4">
                        <div className="flex items-start gap-4 py-2">
                          <img
                            src="/image-fix/pointers/badge-official-original-svgrepo-com.svg"
                            alt=""
                            className="w-10 h-10 flex-shrink-0"
                            loading="lazy"
                            decoding="async"
                          />
                          <p className="text-gray-800 text-sm md:text-base">
                            {item}
                          </p>
                        </div>
                        <div className="h-px bg-gray-300 ml-14" />
                      </div>
                    ))}
                  </div>

                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/40 to-transparent" />
                </div>
              </div>
            </div>
          </div>
          <aside className="hidden md:block sticky top-8">
            <div className="w-full ml-auto">
              <RegisterYourInterestForm />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
